import { Capacitor, registerPlugin } from "@capacitor/core";

export const DRIVER_CALENDAR_ADDED_EVENT = "driver-calendar-added";

const DRIVER_CALENDAR_ADDED_KEY = "driver_calendar_added_orders_v1";
const DriverCalendar = registerPlugin("DriverCalendar");

const readAddedOrderIds = () => {
  try {
    const raw = localStorage.getItem(DRIVER_CALENDAR_ADDED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeAddedOrderIds = (ids) => {
  try {
    localStorage.setItem(DRIVER_CALENDAR_ADDED_KEY, JSON.stringify(Array.from(new Set(ids))));
  } catch {
    // Calendar state is only a local UI hint.
  }
};

export const getDriverCalendarAdded = (orderId) => {
  if (!orderId) return false;
  return readAddedOrderIds().includes(String(orderId));
};

export const markDriverCalendarAdded = (orderId) => {
  if (!orderId) return;
  writeAddedOrderIds([...readAddedOrderIds(), String(orderId)]);
  window.dispatchEvent(new CustomEvent(DRIVER_CALENDAR_ADDED_EVENT, { detail: { orderId } }));
};

export const shouldUseDriverCalendarDownloadLink = () => {
  const capacitor = Capacitor || window?.Capacitor;
  if (!capacitor) return false;
  if (typeof capacitor.isNativePlatform === "function") return Boolean(capacitor.isNativePlatform());
  if (typeof capacitor.getPlatform === "function") return ["ios", "android"].includes(capacitor.getPlatform());
  return false;
};

const cleanText = (value) => String(value || "").replace(/\s+/g, " ").trim();
const cleanLineText = (value) => String(value || "").replace(/[ \t\r\f\v]+/g, " ").trim();

const escapeIcsText = (value) => cleanText(value)
  .replace(/\\/g, "\\\\")
  .replace(/;/g, "\\;")
  .replace(/,/g, "\\,")
  .replace(/\n/g, "\\n");

const escapeIcsMultilineText = (value) => String(value || "")
  .split("\n")
  .map(cleanLineText)
  .filter(Boolean)
  .join("\n")
  .replace(/\\/g, "\\\\")
  .replace(/;/g, "\\;")
  .replace(/,/g, "\\,")
  .replace(/\n/g, "\\n");

const formatIcsDateTime = (date, time) => {
  const safeDate = cleanText(date).replace(/-/g, "");
  const safeTime = cleanText(time || "00:00").replace(":", "").padEnd(6, "0");
  return `${safeDate}T${safeTime}`;
};

const addHoursToIcsDateTime = (date, time, hours) => {
  const [year, month, day] = cleanText(date).split("-").map(Number);
  const [hour = 0, minute = 0] = cleanText(time || "00:00").split(":").map(Number);
  const utcDate = new Date(Date.UTC(year, (month || 1) - 1, day || 1, hour, minute));
  utcDate.setUTCHours(utcDate.getUTCHours() + hours);
  const pad = (value) => String(value).padStart(2, "0");
  return [
    utcDate.getUTCFullYear(),
    pad(utcDate.getUTCMonth() + 1),
    pad(utcDate.getUTCDate()),
    "T",
    pad(utcDate.getUTCHours()),
    pad(utcDate.getUTCMinutes()),
    "00",
  ].join("");
};

const buildOrderRoute = (order) => [
  order.pickup_location_detail || order.pickup_location || order.pickup_area,
  order.dropoff_location_detail || order.destination || order.dropoff_area || order.end_location,
].filter(Boolean).map(cleanText).join(" -> ");

const formatCountValue = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  return cleanText(value);
};

const getOrderRemarks = (order) => [
  order.note,
  order.dispatch_note,
  order.trip_details,
  order.special_requests,
].map(cleanLineText).filter(Boolean).join(" / ");

const normalizeCalendarLanguage = (language) => {
  const value = String(language || "zh_cn").toLowerCase();
  if (value.startsWith("ja")) return "ja";
  if (value.startsWith("en")) return "en";
  if (value === "zh_tw" || value.startsWith("zh-hant")) return "zh_tw";
  return "zh";
};

const calendarLabels = {
  zh: {
    orderNo: "订单号",
    customer: "客户",
    phone: "电话",
    date: "日期",
    time: "时间",
    route: "路线",
    flight: "航班",
    passengers: "人数",
    luggage: "行李数量",
    childSeats: "安全座椅数量",
    notes: "备注",
  },
  zh_tw: {
    orderNo: "訂單號",
    customer: "客戶",
    phone: "電話",
    date: "日期",
    time: "時間",
    route: "路線",
    flight: "航班",
    passengers: "人數",
    luggage: "行李數量",
    childSeats: "安全座椅數量",
    notes: "備註",
  },
  ja: {
    orderNo: "予約番号",
    customer: "お客様名",
    phone: "電話番号",
    date: "日付",
    time: "時刻",
    route: "ルート",
    flight: "便名",
    passengers: "人数",
    luggage: "荷物数",
    childSeats: "チャイルドシート",
    notes: "備考",
  },
  en: {
    orderNo: "Order No",
    customer: "Customer",
    phone: "Phone",
    date: "Date",
    time: "Time",
    route: "Route",
    flight: "Flight",
    passengers: "Passengers",
    luggage: "Luggage",
    childSeats: "Child seats",
    notes: "Notes",
  },
};

const getCalendarLabels = (language) => calendarLabels[normalizeCalendarLanguage(language)] || calendarLabels.zh;

const buildCalendarDescription = (order, language) => {
  const labels = getCalendarLabels(language);
  const route = buildOrderRoute(order);
  const date = order.departure_date;
  const time = order.pickup_time || "00:00";
  const childSeatCount = Number(order.child_seat_count || 0);
  const remarks = getOrderRemarks(order);

  return [
    order.order_no && `${labels.orderNo}: ${cleanText(order.order_no)}`,
    order.customer_name && `${labels.customer}: ${cleanText(order.customer_name)}`,
    order.phone && `${labels.phone}: ${cleanText(order.phone)}`,
    date && `${labels.date}: ${cleanText(date)}`,
    `${labels.time}: ${cleanText(time)}`,
    route && `${labels.route}: ${route}`,
    order.flight_number && `${labels.flight}: ${cleanText(order.flight_number)}`,
    `${labels.passengers}: ${formatCountValue(order.passenger_count, 1)}`,
    `${labels.luggage}: ${formatCountValue(order.luggage_count, 0)}`,
    childSeatCount > 0 && `⚠️${labels.childSeats}: ${childSeatCount}`,
    remarks && `${labels.notes}: ${remarks}`,
  ].filter(Boolean).join("\n");
};

export const buildDriverCalendarNativeEvent = (order, options = {}) => {
  const titleName = cleanText(order.customer_name || order.customer_code || order.order_no || "Order");
  const route = buildOrderRoute(order);
  const date = order.departure_date;
  const time = order.pickup_time || "00:00";
  const [year, month, day] = cleanText(date).split("-").map(Number);
  const [hour = 0, minute = 0] = cleanText(time).split(":").map(Number);
  const startDate = new Date(year, (month || 1) - 1, day || 1, hour, minute);
  const durationHours = Number(order.selected_hours || 0) > 0 ? Number(order.selected_hours) : 2;
  const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);

  return {
    title: `BIG TAXI ${titleName}`,
    startTimestamp: startDate.getTime(),
    endTimestamp: endDate.getTime(),
    location: route ? route.split(" -> ")[0] : "",
    notes: buildCalendarDescription(order, options.language),
  };
};

export const buildDriverCalendarIcs = (order, options = {}) => {
  const titleName = cleanText(order.customer_name || order.customer_code || order.order_no || "Order");
  const route = buildOrderRoute(order);
  const date = order.departure_date;
  const time = order.pickup_time || "00:00";
  const start = formatIcsDateTime(date, time);
  const durationHours = Number(order.selected_hours || 0) > 0 ? Number(order.selected_hours) : 2;
  const end = addHoursToIcsDateTime(date, time, durationHours);
  const description = buildCalendarDescription(order, options.language);

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//BIG TAXI//Driver Order//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${escapeIcsText(order.id || order.order_no || `${date}-${time}`)}@bigtaxi-driver`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z")}`,
    "TZID:Asia/Tokyo",
    `DTSTART;TZID=Asia/Tokyo:${start}`,
    `DTEND;TZID=Asia/Tokyo:${end}`,
    `SUMMARY:${escapeIcsText(`BIG TAXI ${titleName}`)}`,
    route ? `LOCATION:${escapeIcsText(route.split(" -> ")[0])}` : "",
    description ? `DESCRIPTION:${escapeIcsMultilineText(description)}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
};

export const addDriverOrderToCalendar = async (order, options = {}) => {
  if (!order?.id || !order?.departure_date) {
    throw new Error("ORDER_DATE_REQUIRED");
  }

  const {
    createCalendarDownloadLink,
    openCalendarUrl = (url) => window.location.assign(url),
    preferDownloadLink = shouldUseDriverCalendarDownloadLink(),
    resolveDownloadUrl = (url) => url,
    nativeCalendar = DriverCalendar,
    language = "zh_cn",
  } = options;

  if (preferDownloadLink && nativeCalendar?.addEvent) {
    const result = await nativeCalendar.addEvent(buildDriverCalendarNativeEvent(order, { language }));
    if (result?.added === true) {
      markDriverCalendarAdded(order.id);
    }
    return;
  }

  if (preferDownloadLink && createCalendarDownloadLink) {
    const result = await createCalendarDownloadLink(order.id);
    const downloadUrl = result?.download_url;
    if (!downloadUrl) throw new Error("CALENDAR_LINK_MISSING");
    openCalendarUrl(resolveDownloadUrl(downloadUrl));
    markDriverCalendarAdded(order.id);
    return;
  }

  const ics = buildDriverCalendarIcs(order, { language });
  const filename = `BIG_TAXI_${cleanText(order.order_no || order.id).replace(/[^\w-]+/g, "_")}.ics`;
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  markDriverCalendarAdded(order.id);
};
